/**
 * Create Activity Modal
 *
 * Modal dialog for creating Pipedrive activities after sending
 * USI/HMC event invitations. Submits a batch request to the backend
 * and displays real-time progress.
 *
 * Features:
 * - Date picker defaulting to today
 * - Auto-populated event description textarea
 * - Scrollable recipient preview
 * - Progress indicator during creation
 * - Success / error feedback states
 *
 * Used by:
 * - USI Events invitation confirmation step
 * - HMC Events invitation confirmation step
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  X,
  Calendar,
  FileText,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Users,
} from 'lucide-react';
import { Portal } from '@shared/components/ui/Portal';
import { apiClient } from '@shared/services/apiClient';

// ============================================================
// TYPES
// ============================================================

interface Recipient {
  personId: number;
  name: string;
  email: string;
  organization: string;
  orgId?: number;
}

interface CreateActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Event type source */
  eventType: 'usi' | 'hmc';
  /** Default event description */
  defaultDescription?: string;
  /** Recipients to create activities for */
  recipients: Recipient[];
}

type ProgressState = 'idle' | 'creating' | 'complete' | 'error';

interface CreateResult {
  created: number;
  updated: number;
  errors: string[];
}

// ============================================================
// HELPERS
// ============================================================

/** Format today's date as YYYY-MM-DD for the HTML date input */
function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function CreateActivityModal({
  isOpen,
  onClose,
  eventType,
  defaultDescription = '',
  recipients,
}: CreateActivityModalProps) {
  const [date, setDate] = useState(todayIso);
  const [description, setDescription] = useState(defaultDescription);
  const [progressState, setProgressState] = useState<ProgressState>('idle');
  const [progressCount, setProgressCount] = useState(0);
  const [result, setResult] = useState<CreateResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Reset form state whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setDate(todayIso());
      setDescription(defaultDescription);
      setProgressState('idle');
      setProgressCount(0);
      setResult(null);
    }
  }, [isOpen, defaultDescription]);

  // ---- Handlers ----

  const handleClose = useCallback(() => {
    if (progressState === 'creating') return; // prevent close while in-flight
    abortRef.current?.abort();
    onClose();
  }, [progressState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (progressState === 'creating' || recipients.length === 0) return;

    setProgressState('creating');
    setProgressCount(0);
    setResult(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await apiClient.post(
        '/api/pipedrive-activities/create',
        {
          event_type: eventType,
          date,
          description,
          recipients: recipients.map((r) => ({
            person_id: r.personId,
            name: r.name,
            email: r.email,
            organization: r.organization,
            org_id: r.orgId,
          })),
        },
        { signal: controller.signal },
      );

      const raw = response.data as Record<string, unknown>;
      const data: CreateResult = {
        created: (raw.created_count as number) ?? (raw.created as number) ?? 0,
        updated: (raw.updated_persons as number) ?? (raw.updated as number) ?? 0,
        errors: (raw.errors as string[]) ?? [],
      };
      setResult(data);
      setProgressCount(recipients.length);
      setProgressState(data.errors.length > 0 && data.created === 0 ? 'error' : 'complete');
    } catch (err: unknown) {
      if (controller.signal.aborted) return;

      const message =
        err instanceof Error ? err.message : 'Failed to create activities';
      setResult({ created: 0, updated: 0, errors: [message] });
      setProgressState('error');
    }
  }, [progressState, recipients, eventType, date, description]);

  // ---- Early return ----

  if (!isOpen) return null;

  // ---- Render ----

  return (
    <Portal>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      >
        {/* Modal panel */}
        <div
          className="relative w-full max-w-lg mx-4 rounded-xl border border-dark-border bg-dark-card shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            disabled={progressState === 'creating'}
            className="absolute top-3 right-3 p-1.5 text-dark-muted hover:text-dark-text rounded-lg hover:bg-dark-surface transition-colors disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-dark-border">
            <h2 className="text-lg font-semibold text-dark-text">
              Create Pipedrive Activities
            </h2>
            <p className="mt-1 text-xs text-dark-muted">
              Log {eventType.toUpperCase()} event activities for selected contacts
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            {/* Date picker */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-dark-muted mb-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Activity Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={progressState !== 'idle'}
                className="w-full px-3 py-2 text-sm bg-dark-surface border border-dark-border/30 rounded-lg text-dark-text focus:outline-none focus:ring-1 focus:ring-accent-blue/50 focus:border-accent-blue/50 disabled:opacity-50 transition-colors"
              />
            </div>

            {/* Description */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-dark-muted mb-1.5">
                <FileText className="w-3.5 h-3.5" />
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={progressState !== 'idle'}
                rows={3}
                className="w-full px-3 py-2 text-sm bg-dark-surface border border-dark-border/30 rounded-lg text-dark-text placeholder:text-dark-muted resize-none focus:outline-none focus:ring-1 focus:ring-accent-blue/50 focus:border-accent-blue/50 disabled:opacity-50 transition-colors"
                placeholder="Describe the event activity..."
              />
            </div>

            {/* Recipient count */}
            <div className="flex items-center gap-2 text-sm text-dark-muted">
              <Users className="w-4 h-4" />
              <span>
                <strong className="text-dark-text">{recipients.length}</strong>{' '}
                contact{recipients.length !== 1 ? 's' : ''} will be updated
              </span>
            </div>

            {/* Recipient preview */}
            <div className="max-h-32 overflow-y-auto rounded-lg border border-dark-border/30 bg-dark-surface/50">
              {recipients.map((r) => (
                <div
                  key={r.personId}
                  className="px-3 py-1.5 text-xs text-dark-text border-b border-dark-border/10 last:border-b-0"
                >
                  {r.name}
                  <span className="text-dark-muted ml-1.5">
                    &mdash; {r.organization}
                  </span>
                </div>
              ))}
            </div>

            {/* Progress / result feedback */}
            {progressState === 'creating' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-900/15 border border-blue-700/30">
                <Loader2 className="w-4 h-4 text-blue-400 animate-spin shrink-0" />
                <span className="text-xs text-blue-300">
                  Creating activities... ({progressCount}/{recipients.length})
                </span>
              </div>
            )}

            {progressState === 'complete' && result && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-green-900/15 border border-green-700/30">
                <CheckCircle className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                <div className="text-xs text-green-300">
                  <p>
                    Created <strong>{result.created}</strong> / Updated{' '}
                    <strong>{result.updated}</strong> activities
                  </p>
                  {result.errors.length > 0 && (
                    <p className="mt-1 text-yellow-300">
                      {result.errors.length} error(s) occurred
                    </p>
                  )}
                </div>
              </div>
            )}

            {progressState === 'error' && result && (
              <div className="space-y-1 p-3 rounded-lg bg-red-900/15 border border-red-700/30">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  <span className="text-xs font-medium text-red-300">
                    Failed to create activities
                  </span>
                </div>
                {result.errors.map((err, i) => (
                  <p key={i} className="text-xs text-red-300/80 pl-6">
                    {err}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-dark-border">
            <button
              type="button"
              onClick={handleClose}
              disabled={progressState === 'creating'}
              className="px-5 py-2 text-sm font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-xl hover:bg-dark-input disabled:opacity-50 transition-all duration-150"
            >
              {progressState === 'complete' || progressState === 'error'
                ? 'Close'
                : 'Cancel'}
            </button>

            {(progressState === 'idle' || progressState === 'error') && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={recipients.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-accent-blue hover:bg-accent-blue/80 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
              >
                <Send className="w-4 h-4" />
                Create Activities ({recipients.length})
              </button>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}

export default CreateActivityModal;
